import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Hazard, TaskTakeFiveModel } from "../../../shared/models/api/models";
import { TakeFivePage } from "./TakeFivePage";
import { takeFiveDetails } from "./TakeFivePageSelector";
import { takeFiveSectionPathRoute } from "./TakeFiveRoutes";
import { yesNoNaOptions } from "../forms/FormControls";
import { getImageFile, getTakeFiveDetails } from "../../../shared/services/railOperationalFormsApi";
import { Button } from "reactstrap";
import './TakeFive.css'
import TaskTakeFiveQuestions from "./TakeFiveQuestions";
import { formatDateTime } from "../../../shared/models/mapping/mapping";
import { HazardCard } from "./HazardCard";

interface QaRowProps {
    faQuestion: any,
    formDetails: TaskTakeFiveModel,
    translateAnswer?: any,
    colorCode?: 'task' | 'hazards'
}

const QaRow = (props: QaRowProps) => {
    function getKeyValue<T>(obj: T, key: string) {
        let keys = key.split('.');
        let value: any = obj;

        keys.forEach(k => {
            if (value == null || !value.hasOwnProperty(k)) {
                return null;
            }
            value = value[k as keyof T];
        });
        return value;
    }

    let keyVal = getKeyValue(props.formDetails, props.faQuestion.key);
    let answerText = props.translateAnswer ? props.translateAnswer(keyVal) : keyVal;
    
    let answerColor = 'inherit';
    if (props.colorCode === 'task') {
        // The Task section: Yes = green, No = red
        answerColor = answerText === 'Yes' ? 'green' : (answerText === 'No' ? 'red' : 'inherit');
    } else if (props.colorCode === 'hazards') {
        // Hazards Spotted section: Yes = red, No = green
        answerColor = answerText === 'Yes' ? 'red' : (answerText === 'No' ? 'green' : 'inherit');
    }

    return (
        <div className="fa-striped-bg p-2">
            <div className="font-weight-bold tf-qa-row">{props.faQuestion.altValue ?? props.faQuestion.value}</div>
            <div className="pl-2 tf-qa-row" style={{ color: answerColor }}>
                {answerText}
            </div>
        </div>
    );
}

export const Tabs = {
    details: 'details',
    hazards: 'hazards',
}

const TakeFiveDetails = () => {
    const q = TaskTakeFiveQuestions;
    const navigate = useNavigate();
    const params = useParams() as any;
    const [formDetails, setFormDetails] = useState({} as TaskTakeFiveModel);
    const [formId] = useState(parseInt(params.id));
    const [loading, setLoading] = useState(true);
    let results: TaskTakeFiveModel = {} as TaskTakeFiveModel;

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            try {
                if (params.id > 0) {
                    results = await getTakeFiveDetails(params.id);
                    const downloadImages = async () => {
                        if (results.taskHazards?.length) {
                            await Promise.all(
                                results.taskHazards.map(async (hazard: Hazard) => {
                                    if (hazard.imageFileName) {
                                        try {
                                            const downloadedImageFile = await getImageFile(hazard.id!);
                                            const blob = await downloadedImageFile.blob();
                                            const ext = hazard.imageFileName!.split('.').pop()?.toLowerCase();
                                            const mimeType = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png' }[ext!] || 'image/jpeg';
                                            hazard.file = new File([blob], hazard.imageFileName!, { type: mimeType });
                                        } catch (err) {
                                            console.log('Error downloading hazard image', err);
                                        }
                                    }
                                })
                            );
                        }
                    };
                    await downloadImages();

                    if (mounted) {
                        setFormDetails(results);
                        setLoading(false);
                    }
                }
            } catch (error) {
                console.error('Error loading Take Five details:', error);
                if (mounted) {
                    setLoading(false);
                }
            }
        };
        mount();
        return () => { mounted = false };
    }, [formId, results.taskHazards]);

    const goToDashboard = () => {
        navigate(`${takeFiveSectionPathRoute.path}`);
    }

    const translateYesNoNa = (val: number) => {
        return val != null ? yesNoNaOptions.find(x => x.value === val)?.label : '';
    }

    if (loading) {
        return (
            <TakeFivePage selectedPage={takeFiveDetails}>
                <div className="p-4 text-center">Loading...</div>
            </TakeFivePage>
        );
    }

    return (
        <TakeFivePage selectedPage={takeFiveDetails}>
            <div className="tf-details-container">
                <div className="fa-details-container fa-details-body">
                    <div className="tf-details-tab-header">User Data</div>
                    <QaRow faQuestion={q.createdBy} formDetails={formDetails} />
                    <QaRow faQuestion={q.dateCreated} formDetails={formDetails} translateAnswer={formatDateTime} />                    
                    <QaRow faQuestion={q.locationId} formDetails={formDetails} translateAnswer={() => { return formDetails.location }} />
                    <div className="tf-details-tab-header">The Task</div>
                    <QaRow faQuestion={q.taskName} formDetails={formDetails} />
                    <QaRow faQuestion={q.taskScopeUnderstood} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="task" />
                    <QaRow faQuestion={q.procedureExist} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="task" />
                    <QaRow faQuestion={q.taskStepsUnderstood} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="task" />
                    <QaRow faQuestion={q.areToolsAndEquipmentsInGoodOrder} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="task" />
                    <QaRow faQuestion={q.correctPPE} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="task" />
                    <QaRow faQuestion={q.necessaryPermits} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="task" />
                    <QaRow faQuestion={q.clearPlan} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="task" />
                    <QaRow faQuestion={q.canRiskToHandsBeEliminated} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="task" />
                    <div className="tf-details-tab-header">Hazards Spotted</div>
                    <QaRow faQuestion={q.handsPlacedInVicinity} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="hazards" />
                    <QaRow faQuestion={q.equipmentGuardseRemoved} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="hazards" />
                    <QaRow faQuestion={q.mobileEquipmentRisk} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="hazards" />
                    <QaRow faQuestion={q.areHazardousMaterialsInvloved} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="hazards" />
                    <QaRow faQuestion={q.taskPerformedWithoutIsolationProcedure} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="hazards" />
                    <QaRow faQuestion={q.workingAtHeights} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="hazards" />
                    <QaRow faQuestion={q.impactedByOtherActivity} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="hazards" />
                    <QaRow faQuestion={q.environmentalHnSHazards} formDetails={formDetails} translateAnswer={translateYesNoNa} colorCode="hazards" />
                    <div className="tf-details-tab-header">Risk Assessment</div>
                    <div className="font-weight-bold tf-qa-row">Is the risk level equal to / greater than H11</div>
                    <div className="risk-level" style={{ width: "30%", backgroundColor: formDetails.riskLevelEqualOrGreaterThanH11 ? 'red' : 'green' }}>
                        {formDetails.riskLevelEqualOrGreaterThanH11 ? "Yes" : "No"}
                    </div>
                    <div className="tf-details-tab-header">Hazards & Controls</div>
                    {formDetails.taskHazards !== undefined && formDetails?.taskHazards.length > 0 ?
                        <div>
                            {formDetails.taskHazards?.map((item) => (
                                <HazardCard item={item} readonly={true} key={item.id}></HazardCard>
                            ))}
                        </div>
                        :
                        <div className="no-hazards">
                            No hazards submitted
                        </div>
                    }
                </div>
                <div className="fa-details-container fa-details-footer">
                    <Button className="btn-primary m-1" onClick={goToDashboard}>Back to overview</Button>
                </div>
            </div>
        </TakeFivePage>
    );
}

export default TakeFiveDetails;
